<template xmlns:s-modal="http://www.w3.org/1999/html">
  <div class="ma-4">
    <top-navigation
      routeName="bookingOverview"
      :title="
        $t('printTickets.bookingNumber', {
          bookingNumber: bookingInstance.getId(),
        })
      "
    />

    <v-row class="fill-height no-gutters">
      <v-col class="col-8">
        <v-row>
          <!-- buyer -->
          <v-col>
            <booking-buyer-information
              :buyer="bookingInstance.getBuyer()"
              @update:buyer="(newBuyer) => (bookingInstance.buyer = newBuyer)"
            />
          </v-col>

          <v-col>
            <v-row>
              <!-- booking remark -->
              <v-col v-if="hasRemarkPermission" class="col-12 pt-0">
                <v-card outlined>
                  <v-card-title>
                    {{ $t('shopBackendBookingDetail.remark') }}
                  </v-card-title>
                  <v-card-text>
                    <v-form ref="noteForm" @submit.prevent="">
                      <v-row>
                        <v-col class="col-12 pb-0">
                          <v-textarea
                            v-model="bookingInstance.note"
                            outlined
                            :counter="240"
                            :rules="[
                              (v) =>
                                !v ||
                                v.length <= 240 ||
                                $t('shopComment.maxCharacter'),
                            ]"
                          />
                        </v-col>

                        <!-- save button -->
                        <v-col class="col-12 text-right">
                          <v-btn large color="primary" @click="saveNote()">
                            <span class="fal fa-save mr-2" />
                            {{ $t('general.save') }}
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- booking state -->
              <v-col class="col-12 pt-0 pb-0">
                <v-card outlined height="100%">
                  <v-card-title>
                    {{ $t('shopBackendBookingDetail.bookingState') }}
                  </v-card-title>
                  <v-card-text class="py-4">
                    <booking-state
                      :bookingStates="bookingInstance.getBookingStates()"
                      :booking-id="parseInt(bookingInstance.getId())"
                      @updateBookingState="updateNavigationStore()"
                    />
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>

      <!-- payment details -->
      <v-col class="col-4">
        <v-row class="fill-height">
          <v-col>
            <v-card outlined height="100%">
              <v-card-title>
                <v-row>
                  <v-col class="col-10 py-0">
                    {{ $t('shopBackendBookingDetail.paymentDetails') }}
                  </v-col>

                  <!-- warn user if booking is unpaid -->
                  <v-col v-if="!bookingInstance.isPaid()" class="text-right">
                    <span class="fal fa-exclamation-triangle red--text" />
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <payment-details
                  :booking="bookingInstance"
                  @paidCash="init()"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- booking entries -->
    <v-row v-if="!editBookingEntries">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            <v-row class="no-gutters justify-space-between">
              <v-col class="col-auto">
                {{ $t('shopBackendBookingDetail.tickets') }}
              </v-col>
              <!-- edit booking -->
              <v-col class="col-auto text-right">
                <v-btn
                  outlined
                  color="primary"
                  :disabled="!canIEditBasketEntries"
                  @click="editBasketEntries()"
                >
                  <span class="fal fa-pencil mr-2" />
                  <span v-if="canIEditBasketEntries">
                    {{ $t('bookingDetail.editBookingEntries') }}
                  </span>
                  <span
                    v-else-if="
                      bookingInstance.getBookingStates().isAutoConfirmed()
                    "
                  >
                    {{ $t('paymentDetails.payCashFromShopForbidden') }}
                  </span>
                  <span v-else-if="bookingInstance.isPaid()">
                    {{ $t('shopBackendBookingDetail.paid') }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text>
            <!-- booking entries table -->
            <v-row class="no-gutters">
              <v-col class="col-12">
                <v-data-table
                  :headers="headers"
                  :items="bookingInstance.getBookingEntries()"
                  :items-per-page="-1"
                  :no-data-text="$t('dataTable.noDataAvailable')"
                  :no-results-text="$t('dataTable.noResultFound')"
                  :footer-props="{
                    itemsPerPageText: $t('general.rowsPerPage'),
                  }"
                  :loading="false"
                  :loading-text="$t('general.loadingData')"
                >
                  <template slot="item" slot-scope="props">
                    <tr>
                      <!-- checkbox -->
                      <td>
                        <v-checkbox
                          :input-value="props.item.isSelected"
                          :disabled="
                            props.item.hasBeenCancelled() ||
                            props.item.getRequiredEntry() !== null
                          "
                          color="primary"
                          @change="selectBookingEntry(props.item)"
                        />
                      </td>

                      <!-- cancellation infos -->
                      <td>
                        <!-- has been cancelled -->
                        <span v-if="props.item.hasBeenCancelled()">
                          <div class="red--text">
                            {{ $t('bookingManagement.cancelledAt') }}:
                            {{
                              props.item.getCancellation().getCancellationDate()
                            }}
                          </div>
                          <div>
                            {{ $t('bookingManagement.cancelledBy') }}:
                            {{
                              props.item
                                .getCancellation()
                                .getUser()
                                .getFullName()
                            }}
                          </div>
                          <div>
                            {{ $t('v4.giveMeAStornoReason') }}:
                            {{ props.item.getCancellation().getReason() }}
                          </div>
                        </span>

                        <!-- not yet cancelled and is self cancelable -->
                        <span
                          v-else-if="props.item.isSelfCancelable()"
                          class="green--text"
                        >
                          <v-row class="align-center">
                            <v-col class="col-1">
                              <span class="fal fa-check mr-2" />
                            </v-col>
                            <v-col>
                              {{ $t('bookingManagement.flexCanBeCancelled') }}
                            </v-col>
                          </v-row>
                        </span>

                        <!-- not self cancellable -->
                        <span v-else>
                          <v-row class="align-center">
                            <v-col class="col-1 red--text">
                              <span class="fal fa-exclamation-triangle mr-2" />
                            </v-col>
                            <v-col>
                              {{ $t('bookingManagement.stornoNotPossible') }}
                            </v-col>
                          </v-row>
                        </span>
                      </td>

                      <!-- external ticket provider state -->
                      <td>
                        <!-- skidata only: order id -->
                        <div v-if="props.item.getSkidata()">
                          {{ $t('skidata.orderId') }}:
                          {{ props.item.getSkidata().getOrderId() }}
                        </div>

                        <!-- skidata only: revocation id -->
                        <div v-if="props.item.getSkidata()">
                          {{
                            $t('shopBackendBookingDetail.skidataLockNumber')
                          }}: {{ props.item.getSkidata().getRevocationId() }}
                        </div>

                        <!-- state -->
                        <div v-if="props.item.getExternalTicketProvider()">
                          <span
                            v-if="
                              props.item
                                .getExternalTicketProvider()
                                .isGreenState()
                            "
                          >
                            <span class="fal fa-check green--text mx-1" />
                          </span>

                          <span v-else>
                            <span
                              class="fal fa-exclamation-triangle red--text ml-2 mr-1"
                            />
                          </span>
                          {{
                            props.item
                              .getExternalTicketProvider()
                              .getStateDescription()
                          }}
                        </div>
                      </td>

                      <!-- user -->
                      <td
                        :class="{
                          'line-through': props.item.hasBeenCancelled(),
                        }"
                      >
                        <span v-if="props.item.getUser()">
                          {{ props.item.getUser().getFullName() }}
                        </span>
                      </td>

                      <!-- ticket description -->
                      <td
                        :class="{
                          'line-through': props.item.hasBeenCancelled(),
                        }"
                      >
                        {{ props.item.getProductTranslation() }},
                        {{ props.item.getProductDefinitionTranslation() }}
                      </td>

                      <!-- medium -->
                      <td
                        class="clickable"
                        @click="showPickupBarcode(props.item)"
                      >
                        <!-- send -->
                        <div v-if="props.item.getMedia() === 'send'">
                          <span class="fal fa-mailbox fa-2x" />
                          <p class="mb-0">{{ $t('availableMedia.send') }}</p>
                        </div>
                        <!-- send -->
                        <div v-else-if="props.item.getMedia() === 'onSite'">
                          <span class="fal fa-cash-register fa-2x" />
                          <p class="mb-0">{{ $t('availableMedia.onSite') }}</p>
                        </div>
                        <v-img
                          v-else
                          class="mx-0 primary"
                          :src="getMediumUrl(props.item.getMedia())"
                          aspect-ratio="undefined"
                          height="40"
                          width="80"
                          contain
                        />
                      </td>

                      <!-- valid from -->
                      <td
                        :class="{
                          'line-through': props.item.hasBeenCancelled(),
                        }"
                      >
                        {{ props.item.getStartDate() | formatDate }}
                      </td>

                      <!-- promo codes -->
                      <td
                        :class="{
                          'line-through': props.item.hasBeenCancelled(),
                        }"
                      >
                        {{ props.item.getPromoCode() }}
                      </td>

                      <!-- price of the booking entry (ticket) -->
                      <td
                        :class="{
                          'line-through': props.item.hasBeenCancelled(),
                        }"
                      >
                        <!-- has promo code -->
                        <span v-if="props.item.getPromoCode()">
                          <span class="line-through mr-2">
                            {{ props.item.getOriginalPrice() | currency }}
                          </span>
                          <span>
                            {{ props.item.getPrice() | currency }}
                          </span>
                        </span>
                        <!-- no promo code -->
                        <span v-else>
                          {{ props.item.getPrice() | currency }}
                        </span>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-col>

              <v-col class="col-12">
                <!-- cancellation button -->
                <v-btn
                  color="error"
                  large
                  :disabled="selectedBookingEntries.length <= 0"
                  outlined
                  @click="openCancellationModal()"
                >
                  <i class="fas fa-times mr-2" />
                  {{ $t('shopBackendBookingDetail.cancelMarkedTickets') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- edit booking entries -->
    <v-row v-if="editBookingEntries">
      <!-- header -->
      <v-col class="col-12">
        <v-card outlined>
          <v-card-title>
            <v-row class="no-gutters justify-space-between">
              <v-col class="col-auto">
                {{ $t('bookingDetail.editBookingEntries') }}
              </v-col>
              <v-col class="col-auto text-right">
                <!-- cancel edit -->
                <v-btn outlined color="red" @click="cancelEditBasketEntries()">
                  <span class="fal fa-times mr-2" />
                  {{ $t('bookingDetail.closeEditBookingEntries') }}
                </v-btn>

                <!-- save edit -->
                <v-btn
                  outlined
                  color="primary"
                  class="ml-2"
                  @click="saveNewBasket()"
                >
                  <span class="fal fa-check mr-2" />
                  {{ $t('adminBasket.saveChanges') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text class="px-0">
            <v-row class="no-gutters">
              <v-col class="col-12">
                <admin-basket-component
                  :basket-instance="adminBasket"
                  :booking-instance="bookingInstance"
                  :selected-user-bookings="userBookings"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- cancellation modal -->
    <s-modal
      ref="cancellationModal"
      :hideOnOk="false"
      :size="1"
      :headerText="$t('bookingManagement.reallyStorno')"
      :okText="$t('bookingManagement.storno')"
      @ok="cancelSelectedBookingEntries()"
    >
      <div v-if="bookingInstance">
        <!-- infos -->
        <v-row class="justify-center">
          <!-- if booking is paid -->
          <v-col v-if="bookingInstance.isPaid()" class="col-12">
            <div class="subheading mb-2">
              <span class="fa fa-info-circle info--text mr-2" />
              {{ $t('v3.usedTicketCannotBeCancelled') }}
            </div>
            <div class="subheading mb-2">
              <span class="fa fa-info-circle info--text mr-2" />
              {{ $t('bookingManagement.ticketStornoExplanaition') }}
            </div>
            <div class="subheading">
              <span class="fa fa-info-circle info--text mr-2" />
              {{ $t('bookingDetail.stornoForZero') }}
            </div>
          </v-col>

          <!-- unpaid booking -->
          <v-col v-else class="col-12">
            <div class="mb-4">
              <span class="fal fa-info-circle blue--text mr-2" />
              {{ $t('bookingDetail.unpaidBookingToReleaseCapacities') }}
            </div>
            <!-- no voucher will be created -->
            <div>
              <span class="fal fa-info-circle blue--text mr-2" />
              {{ $t('bookingDetail.noVoucherWillBeCreated') }}
            </div>
          </v-col>
        </v-row>

        <!-- storno form -->
        <v-form ref="cancellationForm" @submit.prevent="">
          <!-- storno only part -->
          <v-row class="wrap mt-4 border-top pt-4 border-bottom pb-4">
            <v-col v-if="bookingInstance.isPaid()" class="col-5">
              <div class="subheading pt-2">
                {{ $t('bookingDetail.voucherAmount') }}:
              </div>
            </v-col>

            <!-- input field for amount -->
            <v-col v-if="bookingInstance.isPaid()" class="col-4 pb-0">
              <v-text-field
                v-model="cancellationAmount"
                :rules="[
                  (v) => !!v || v === 0 || $t('v4.stornoAmountRequired'),
                ]"
                :label="$t('v4.amount')"
                validate-on-blur
                outlined
                type="number"
              />
            </v-col>

            <!-- max amount -->
            <v-col v-if="bookingInstance.isPaid()" class="col-3 pb-0">
              <div class="subheading mt-6">
                /
                {{
                  bookingInstance.getTotalPriceOfSelectedBookingEntries()
                    | currency
                }}
              </div>
            </v-col>

            <!-- voucher for -->
            <v-col v-if="bookingInstance.isPaid()" class="col-5">
              {{ $t('bookingDetail.voucherFor') }}:
            </v-col>
            <v-col
              v-if="bookingInstance.getBuyer() && bookingInstance.isPaid()"
              class="col-7 font-weight-bold"
            >
              {{ bookingInstance.getBuyer().getFullName() }}
            </v-col>

            <!-- reason -->
            <v-col class="col-12 col-md-5 align-self-center pt-0">
              <div class="subheading">{{ $t('v4.giveMeAStornoReason') }}:</div>
            </v-col>

            <!-- reason input field -->
            <v-col class="col-12 col-md-7 pt-0">
              <v-text-field
                v-model="cancellationReason"
                :rules="[(v) => !!v || $t('v4.stornoReasonRequired')]"
                :label="$t('v4.giveMeAStornoReason')"
                validate-on-blur
                outlined
              />
            </v-col>
          </v-row>
        </v-form>
      </div>
    </s-modal>

    <!-- reminder to cancel on skidata cash desk manually -->
    <s-modal
      ref="reminderModal"
      :size="2"
      :headerText="$t('shopBackendBookingDetail.doNotForget')"
      hide-cancel-button
      @ok="cancellationResponse = null"
    >
      <v-row>
        <!-- general information -->
        <v-col class="col-12">
          <span class="fal fa-check mr-2 green--text" />
          {{ $t('shopBackendBookingDetail.cancellationGenerallyOk') }}
        </v-col>

        <v-col class="col-12">
          <span class="fal fa-times red--text mr-2" />
          {{
            $t('shopBackendBookingDetail.thirdPartySystemCancellationFailed')
          }}
        </v-col>
      </v-row>

      <!-- iterate not successful tickets -->
      <v-row v-if="cancellationResponse">
        <!-- header -->
        <v-col class="col-12">
          <v-row class="border-bottom font-weight-black">
            <!-- user name -->
            <v-col class="col-3">
              {{ $t('shopBackendBookingDetail.name') }}
            </v-col>

            <!-- ticket description -->
            <v-col class="col-3">
              {{ $t('shopBackendBookingDetail.ticket') }}
            </v-col>

            <!-- system -->
            <v-col class="col-2">
              {{ $t('shopBackendBookingDetail.system') }}
            </v-col>

            <!-- skidata reservation and revocation number -->
            <v-col class="col-4">
              {{ $t('bookingManagement.skidata') }}
            </v-col>
          </v-row>
        </v-col>

        <!-- iterate errors -->
        <v-col
          v-for="(cancellationError, errorKey) in cancellationResponse"
          :key="errorKey"
          class="col-12"
        >
          <v-row v-if="bookingInstance.getBookingEntryById(errorKey)">
            <!-- user name -->
            <v-col class="col-3">
              <span class="fal fa-exclamation-triangle red--text mr-2" />
              {{
                bookingInstance
                  .getBookingEntryById(errorKey)
                  .getUser()
                  .getFullName()
              }}
            </v-col>

            <!-- ticket description -->
            <v-col class="col-3">
              {{
                bookingInstance.getBookingEntryById(errorKey).getTranslation()
              }}
            </v-col>

            <!-- system -->
            <v-col class="col-2">
              <!-- swisspass -->
              <span
                v-if="
                  cancellationError.hasOwnProperty('swisspass') &&
                  cancellationError.swisspass === false
                "
              >
                Swisspass
              </span>

              <!-- skidata -->
              <span
                v-if="
                  cancellationError.hasOwnProperty('skidata') &&
                  cancellationError.skidata === false
                "
              >
                Skidata
              </span>

              <!-- axess -->
              <span
                v-if="
                  cancellationError.hasOwnProperty('axess') &&
                  cancellationError.axess === false
                "
              >
                Axess
              </span>
            </v-col>

            <!-- skidata reservation and revocation number -->
            <v-col
              v-if="bookingInstance.getBookingEntryById(errorKey).getSkidata()"
              class="col-4"
            >
              <div>
                {{ $t('skidata.orderId') }}:
                {{
                  bookingInstance
                    .getBookingEntryById(errorKey)
                    .getSkidata()
                    .getOrderId()
                }}
              </div>
              <div>
                {{ $t('shopBackendBookingDetail.skidataLockNumber') }}:
                {{
                  bookingInstance
                    .getBookingEntryById(errorKey)
                    .getSkidata()
                    .getRevocationId()
                }}
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </s-modal>

    <!-- barcode modal -->
    <s-modal
      ref="barcodeModal"
      :size="2"
      :header-text="
        currentMediaProvider && currentMediaProvider.getTypeDescription()
      "
      hide-cancel-button
      :ok-text="$t('general.close')"
    >
      <v-row v-if="currentMediaProvider">
        <!-- (1) Print@Home: print barcode from axess or skidata -->
        <pickup-barcode
          v-if="currentMediaProvider.getBarcode()"
          :barcode="currentMediaProvider.getBarcode()"
          :barcode-mode="bookingInstance.getDestination().getBarcodeMode()"
        />
        <!-- (2) Swisspass, Axess or Skidata cards -->
        <!-- card number -->
        <v-row
          v-if="currentMediaProvider.getCard()"
          :variable="(card = currentMediaProvider.getCard())"
        >
          <!-- card holder -->
          <v-col
            v-if="currentMediaProvider.getType() === 'swisspass'"
            class="col-12 pt-0"
          >
            {{ card.getFullName() }}
          </v-col>
          <!-- card number -->
          <v-col v-if="card.getCardNumber()" class="col-12 pt-0">
            <span class="fal fa-credit-card mr-1" />
            {{ card.getCardNumber() }}
          </v-col>
          <!-- GA -->
          <v-col
            v-if="currentMediaProvider.getType() === 'swisspass'"
            class="col-12 pt-0"
          >
            <!-- valid GA -->
            <span v-if="card.hasValidGA()">
              <span class="fal fa-check mr-1 green--text" />
              {{ $t('bookingDetail.validGA') }}
            </span>
            <!-- invalid GA -->
            <span v-else>
              <span class="fal fa-times mr-1 error--text" />
              {{ $t('bookingDetail.invalidGA') }}
            </span>
          </v-col>
          <!-- HTA -->
          <v-col
            v-if="currentMediaProvider.getType() === 'swisspass'"
            class="col-12 pt-0"
          >
            <!-- valid HTA -->
            <span v-if="card.hasValidGA()">
              <span class="fal fa-check mr-1 green--text" />
              {{ $t('bookingDetail.validHTA') }}
            </span>
            <!-- invalid HTA -->
            <span v-else>
              <span class="fal fa-times mr-1 error--text" />
              {{ $t('bookingDetail.invalidHTA') }}
            </span>
          </v-col>
        </v-row>
      </v-row>
    </s-modal>
  </div>
</template>

<script>
import BookingBuyerInformation from './detail/BuyerInformation'
import Booking from '../../classes-shared/bookings/Booking'
import definitions from '../../../definitions'
import PickupBarcode from './PickupBarcode'
import TopNavigation from '../../components-shared/menus/TopNavigation'
import BookingState from './detail/BookingState'
import PaymentDetails from './detail/PaymentDetails'
import AdminBasketComponent from '@/components-internal/booking/create/AdminBasket'
import AdminBasket from '@/classes-shared/basket/AdminBasket.js'
import UserBookings from '@/classes-shared/bookings/UserBookings'
import Bookings from '@/classes-shared/bookings/Bookings'

export default {
  name: 'BookingDetail',

  components: {
    AdminBasketComponent,
    PaymentDetails,
    BookingState,
    TopNavigation,
    PickupBarcode,
    BookingBuyerInformation,
  },

  data() {
    return {
      bookingInstance: null,
      // cancellation variables
      cancellationAmount: null,
      cancellationReason: null,
      cancellationResponse: null,
      // display CreateBookingOverview.vue
      editBookingEntries: false,
      adminBasket: new AdminBasket(),
      currentMediaProvider: null,
    }
  },

  computed: {
    headers() {
      return [
        {
          text: '',
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('shopBackendBookingDetail.cancelTicket'),
          sortable: false,
        },
        {
          text: this.$t('bookingManagement.externalTicketProvider'),
          sortable: false,
        },
        {
          text: this.$t('shopBackendBookingDetail.name'),
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('shopBackendBookingDetail.ticket'),
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('shopBackendBookingDetail.dataMedium'),
          sortable: false,
        },
        {
          text: this.$t('shopBackendBookingDetail.validFrom'),
          sortable: false,
        },
        {
          text: this.$t('shopBackendBookingDetail.promoCode'),
          sortable: false,
        },
        {
          text: this.$t('shopBackendBookingDetail.price'),
          align: 'left',
          sortable: true,
          value: 'price',
        },
      ]
    },

    // short cut
    selectedBookingEntries() {
      if (this.bookingInstance)
        return this.bookingInstance.getSelectedBookingEntries()
      return []
    },
    // cast booking and user to UserBookings.js instance (used in admin basket)
    userBookings() {
      if (this.bookingInstance && this.bookingInstance.getBuyer()) {
        let userBookings = new UserBookings(this.bookingInstance.getBuyer())
        userBookings.bookings = new Bookings().userBookings = [
          this.bookingInstance,
        ]
        return userBookings
      }
      return null
    },
    canIEditBasketEntries() {
      return (
        !this.bookingInstance.isPaid() &&
        !this.bookingInstance.getBookingStates().isAutoConfirmed()
      )
    },
  },

  asyncComputed: {
    // short cut
    async hasRemarkPermission() {
      if (this.$store.getters.getAppUserInstance()) {
        return await this.$store.getters
          .getAppUserInstance()
          .doIHavePermissionInTheCurrentDestinationForKey(
            definitions.permissions.frontend
              .FRONTEND_ACCESS_SHOP_REMARK_MANAGEMENT
          )
      }
      return false
    },
  },

  created() {
    this.init()
  },

  methods: {
    // entry point of the view
    async init() {
      // close view to edit basket entries
      this.editBookingEntries = false
      if (!this.$route.params.id) {
        /* global EventBus */
        EventBus.$emit('notify', this.$t('shopBackendBookingDetail.missingId'))
      }

      this.bookingInstance = new Booking({ id: this.$route.params.id })
      await this.bookingInstance.fetchBooking()
      this.updateNavigationStore()
    },
    // Show AdminBasket.vue component to edit the booking entries
    async editBasketEntries() {
      const basketUuid = this.bookingInstance.getBasketUuid()
      if (!basketUuid) {
        throw new Error('Unexpected Error: No basket uuid found!')
      }
      // loading basket from api
      await this.adminBasket.getBasketByUuid(basketUuid)
      this.editBookingEntries = true
    },
    cancelEditBasketEntries() {
      this.editBookingEntries = false
      this.adminBasket = new AdminBasket()
    },
    selectBookingEntry(selectedBookingEntry) {
      // select clicked booking entry
      selectedBookingEntry.isSelected = !selectedBookingEntry.isSelected
      // select possible requirer
      let bookingEntries = this.bookingInstance.getBookingEntries()
      for (let i = 0; i < bookingEntries.length; i++) {
        let bookingEntry = bookingEntries[i]
        if (bookingEntry.getRequiredEntry() === selectedBookingEntry.getId()) {
          bookingEntry.isSelected = !bookingEntry.isSelected
        }
      }
    },

    async showPickupBarcode(bookingEntry) {
      // we do have a barcode
      this.currentMediaProvider = bookingEntry.getMediaProvider()
      if (this.currentMediaProvider) {
        const card = this.currentMediaProvider?.getCard()
        // eventually update swisspass abos
        if (card && this.currentMediaProvider.getType() === 'swisspass') {
          await card.updateSwisspass()
        }
        this.$refs.barcodeModal.show()
      }
    },

    async openCancellationModal() {
      if (this.bookingInstance.isPaid())
        this.cancellationAmount =
          (await this.bookingInstance.getTotalPriceOfSelectedBookingEntries()) /
          100
      else this.cancellationAmount = 0 // unpaid bookings should be cancelled with amount of 0
      this.$refs.cancellationModal.show()
    },

    async cancelSelectedBookingEntries() {
      if (this.$refs.cancellationForm.validate()) {
        // reset cancellation response
        this.cancellationResponse = null

        // an unpaid booking should only be cancelled with amount of 0
        if (!this.bookingInstance.isPaid() && this.cancellationAmount > 0) {
          EventBus.$emit(
            'notify',
            this.$t('bookingDetail.unpaidBookingCannotHaveAnAmount')
          )
          throw new Error(
            'Unpaid booking shall be cancelled with an amount of 0.'
          )
        }

        // cancel the booking entries
        let response = await this.bookingInstance.cancelSelectedBookingEntries(
          this.cancellationAmount,
          this.cancellationReason
        )

        if (response === true) {
          // successfull cancellation
          EventBus.$emit(
            'notify',
            this.$t('shopBackendBookingDetail.cancellationWasSuccessful'),
            'success'
          )
        } else if (response === false) {
          // unknown error
          /* global EventBus */
          EventBus.$emit(
            'notify',
            this.$t(
              'shopBackendBookingDetail.cancellationFaildForUnknownReason'
            )
          )
        } else {
          // response body from api to show to the user
          this.cancellationResponse = response
          this.$refs.reminderModal.show()
        }

        // close modal
        this.$refs.cancellationModal.hide()

        // reload booking instance
        await this.bookingInstance.fetchBooking()
      }
    },
    saveNote() {
      if (!this.$refs.noteForm.validate()) return
      this.bookingInstance.saveNote()
      this.updateNavigationStore()
    },
    // change booking entries with a new basket
    async saveNewBasket() {
      const oldBookingId = this.bookingInstance.getId()
      const newBookingId = await this.bookingInstance.saveNewBasket()
      if (newBookingId) {
        this.bookingInstance = new Booking({ id: newBookingId })
        await this.bookingInstance.fetchBooking()
        this.cancelEditBasketEntries()
        // update navigation store
        this.updateNavigationStore(this.bookingInstance, oldBookingId)
        // update route with new booking id
        await this.$router.replace({
          name: 'bookingDetailView',
          params: { id: newBookingId },
        })
      }
    },
    updateNavigationStore(
      newBookingInstance = this.bookingInstance,
      oldBookingId = this.bookingInstance.getId()
    ) {
      let navigationStore = this.$store.getters.getNavigationStores()
      let navigationStoreObject = navigationStore.getNavigationStoreByRouteName(
        'bookingOverview'
      )?.storeObject
      navigationStoreObject?.selectedUserBookings?.bookings?.replaceUserBooking(
        oldBookingId,
        newBookingInstance
      )
    },
    getMediumUrl(medium) {
      try {
        return require(`@/assets/icons/ticketMedia/${medium}.svg`)
      } catch (e) {
        return ''
      }
    },
  },
}
</script>
